/**
 * @module CloudGCUMetricsQuery
 *
 * This module handles the GraphQL queries related to cloud metrics.
 * It defines the hook `useCloudGCUMetricsQuery` to interact with
 * GraphOS Cloud metrics within an account.
 */

import { gql, useQuery } from '@apollo/client';

import {
  GraphOSMetrics,
  GraphOSMetricsVariables,
  Resolution,
  Scalars,
} from 'src/lib/graphqlTypes/types';

/**
 * GraphQL query to fetch GraphOS Cloud metrics for a specific graph ID,
 * timeframe, and resolution.
 * @type {DocumentNode}
 */
const CLOUD_GCU_METRICS_QUERY = gql`
  query GraphOSMetrics(
    $filter: ServiceGraphosCloudMetricsFilter
    $graphId: ID!
    $from: Timestamp!
    $resolution: Resolution!
  ) {
    graph(id: $graphId) {
      id
      statsWindow(from: $from, resolution: $resolution) {
        graphosCloudMetrics(filter: $filter) {
          timestamp
          metrics {
            responseSize
            responseSizeThrottled
            routerOperations
            routerOperationsThrottled
            subgraphFetches
            subgraphFetchesThrottled
          }
        }
      }
    }
  }
`;

/**
 * Hook to fetch GraphOS Cloud metrics for a specific graph ID, timeframe,
 * and resolution.
 *
 * @param {ServiceGraphosCloudMetricsFilter} filter - The filter options to apply to the query. Currently we only support filtering by schemaTag.
 * @param {Scalars['ID']} graphId - The ID of the graph for which to fetch metrics.
 * @param {Scalars['Timestamp']} from - The timestamp marking the start of the timeframe.
 * @param {Resolution} resolution - The resolution of the data.
 * @param {boolean} skip - Whether to skip executing the query initially.
 * @returns {Object} - An object containing the following keys:
 *                     - data: Data returned from the query.
 *                     - loading: Loading state of the query.
 *                     - error: Errors if the query fails.
 */
export const useCloudGCUMetricsQuery = (
  filter: string,
  graphId: string,
  from: Scalars['Timestamp']['output'],
  resolution: Resolution,
  skip: boolean,
) => {
  return useQuery<GraphOSMetrics, GraphOSMetricsVariables>(
    CLOUD_GCU_METRICS_QUERY,
    {
      variables: {
        filter: {
          schemaTag: filter,
        },
        graphId,
        from,
        resolution,
      },
      skip,
    },
  );
};
